import { useForm } from 'react-hook-form'

import { PollStatus, UpdatePollInput } from '@bettermode/common/gql/generated'
import { Poll } from '@tribeplatform/gql-client/types'
import { usePoll, useUpdatePoll } from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'

import { T, useI18n } from '../i18n/index.js'

import { PollForm } from './form/PollForm.js'
import type { PollFormValues } from './form/types.js'
import { PollFormActionProps } from './types.js'
import { onError, pickKeys } from './utils.js'

export interface EditPollFormProps extends PollFormActionProps {
  id: Poll['id']
}

export function EditPollForm(props: EditPollFormProps) {
  const { id } = props
  const poll = usePoll(id)

  if (poll.status === PollStatus.Closed) {
    return (
      <Alert status="info">
        <T
          id="Poll.UnableToUpdateClosedPoll"
          defaultMessage="This poll has already been closed. Settings can't be updated after a poll is closed."
        />
      </Alert>
    )
  }

  return <EditPollFormContent {...props} />
}

export interface EditPollFormContentProps
  extends Pick<PollFormActionProps, 'onCancel' | 'onSuccess'> {
  poll: Poll
}

function EditPollFormContent({ id, onCancel, onSuccess }: EditPollFormProps) {
  const poll = usePoll(id)
  const i18n = useI18n()

  const { mutateAsync: updatePoll, isLoading } = useUpdatePoll({
    fields: 'all',
    useMutationOptions: {
      onSuccess(data) {
        onSuccess?.(data)
      },
      onError: onError(i18n),
    },
  })

  // we're not going to fill the opensAt and closesAt fields because we don't want to send them again to the server, we handle the changing and showing of them in the form
  const form = useForm<PollFormValues>({
    defaultValues: poll
      ? {
          title: poll.title,
          options: poll.options.map(({ id, label: value }) => ({
            id: id.toString(),
            value,
          })),
          maxSelections: poll.maxSelections.toString(),
          opensAt: poll.opensAt ? new Date(poll.opensAt) : undefined,
          closesAt: poll.closesAt ? new Date(poll.closesAt) : undefined,
        }
      : {
          title: '',
          options: [],
          maxSelections: '1',
        },
  })

  const onSubmit = ({
    title,
    options,
    maxSelections,
    opensAt,
    closesAt,
  }: PollFormValues) => {
    const dirtyFields = Object.keys(
      form.formState.dirtyFields,
    ) as (keyof UpdatePollInput)[]

    // we're doing this because the arrayField.remove() doesn't trigger the dirty fields, and also handling this with form.setValue doesn't work neither
    if (poll.options.length !== options.length) {
      dirtyFields.push('options')
    }

    if (dirtyFields.length === 0) {
      return onSuccess?.(poll)
    }

    return updatePoll({
      id: poll.id,
      input: pickKeys(
        {
          title,
          options: options.map(({ value }) => ({
            label: value,
          })),
          maxSelections: Number(maxSelections),
          opensAt: opensAt?.toUTCString(),
          closesAt: closesAt === null ? null : closesAt?.toUTCString(), // null means we set null value for it in the server, but undefined means we're not going to send it to the server
        },
        dirtyFields,
      ),
    })
  }

  return (
    <PollForm
      form={form}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isLoading={isLoading}
      mode="edit"
      hasAlreadyVoted={poll.totalParticipants > 0}
    />
  )
}
